import React, { useState, useEffect, useCallback } from "react";
import "../page/partnerList.css";

import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import PaginationFooter from "../../../components/footer/paginationFooter";
import PartnerHeader from "../component/partnerHeader";
import ModalAddPartner from "../component/modalAddPartner";
import PartnerTable from "../component/partnerTable";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";

const PartnerList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddPartner, setIsOpenModalAddPartner] = useState(false);

  const [selectedPartnerData, setSelectedPartnerData] = useState({
    partner_id: "",
    partner_name: "",
    partner_address: "",
    partner_city: "",
    partner_phone_number: "",
    partner_contact_person_name: "",
    partner_contact_person_phone_number: "",
  });
  const [partnerData, setPartnerData] = useState(null);
  const [isOpenModalDeletePartner, setIsOpenModalDeletePartner] =
    useState(false);
  const [idPartnerToDelete, setIdPartnerToDelete] = useState("");
  const [searchPartnerKeyword, setSearchPartnerKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewPartner = () => {
    resetSelectedPartnerDataValue();
    setIsOpenModalAddPartner(true);
  };

  const onEditPartner = (partnerData) => {
    setSelectedPartnerData(partnerData);
    setIsOpenModalAddPartner(true);
  };

  const onDeletePartner = (partnerData) => {
    setIsOpenModalDeletePartner(true);
    setIdPartnerToDelete(partnerData.partner_id);
  };

  const onDeletePartnerConfirmed = () => {
    setIsOpenModalDeletePartner(false);
    deletePartner();
  };

  const onCloseModalDeletePartner = () => {
    setIdPartnerToDelete("");
    setIsOpenModalDeletePartner(false);
  };

  const onCloseModalAddPartner = () => {
    resetSelectedPartnerDataValue();
    setIsOpenModalAddPartner(false);
  };

  const onSubmitNewPartner = (partnerData, isEditData) => {
    setIsOpenModalAddPartner(false);
    if (isEditData) {
      editPartner(partnerData);
    } else {
      createNewPartner(partnerData);
    }
  };

  const resetSelectedPartnerDataValue = () => {
    setSelectedPartnerData({
      partner_id: "",
      partner_name: "",
      partner_address: "",
      partner_city: "",
      partner_phone_number: "",
      partner_contact_person_name: "",
      partner_contact_person_phone_number: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getPartnerData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");
      let url = Constant.BASE_API_URL + "/partner?page=" + page;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.partners) {
        setPartnerData([]);
      } else {
        setPartnerData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setPartnerData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewPartner = async (partnerData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/partner", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          partner_name: partnerData.partner_name,
          partner_address: partnerData.partner_address,
          partner_city: partnerData.partner_city,
          partner_phone_number: partnerData.partner_phone_number,
          partner_contact_person_name: partnerData.partner_contact_person_name,
          partner_contact_person_phone_number:
            partnerData.partner_contact_person_phone_number,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getPartnerData();
    }
  };

  const editPartner = async (partnerData) => {
    try {
      setIsLoading(true);
      const partnerId = partnerData.partner_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/partner/" + partnerId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            partner_name: partnerData.partner_name,
            partner_address: partnerData.partner_address,
            partner_city: partnerData.partner_city,
            partner_phone_number: partnerData.partner_phone_number,
            partner_contact_person_name:
              partnerData.partner_contact_person_name,
            partner_contact_person_phone_number:
              partnerData.partner_contact_person_phone_number,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getPartnerData();
    }
  };

  const deletePartner = async () => {
    try {
      setIsLoading(true);
      const partnerId = idPartnerToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/partner/" + partnerId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdPartnerToDelete("");
      getPartnerData();
    }
  };

  const searchPartner = (searchText) => {
    setSearchPartnerKeyword(searchText);
    setPage(1);
  };

  const getPartnerSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/partner/search");
      url.searchParams.append("keyword", searchPartnerKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!data.partners) {
        setPartnerData(null);
      } else {
        setPartnerData(data);
      }
    } catch (error) {
      setPartnerData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchPartnerKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchPartnerKeyword !== "") {
      getPartnerSearchData();
    } else {
      getPartnerData();
    }
  }, [searchPartnerKeyword, getPartnerData, getPartnerSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Partner</h1>
      <PartnerHeader
        onAddPartnerClicked={addNewPartner}
        onSearchPartnerEdited={searchPartner}
      />
      <PartnerTable
        page={page}
        partners={partnerData.partners}
        onEditPartnerClicked={onEditPartner}
        onDeletePartnerClicked={onDeletePartner}
      />
      <ModalAddPartner
        isShowModal={isOpenModalAddPartner}
        onCloseModal={onCloseModalAddPartner}
        onSubmitData={onSubmitNewPartner}
        partnerObject={selectedPartnerData}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeletePartner}
        onCloseModal={onCloseModalDeletePartner}
        onDeleteConfirmed={onDeletePartnerConfirmed}
        idToDelete={idPartnerToDelete}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
    </div>
  );
};

export default PartnerList;
